import { useEffect, useRef } from 'react'
import { Animated, Easing, StyleSheet, View } from 'react-native'
import { LinearGradient } from 'expo-linear-gradient'
import { DURATION } from '../../lib/theme/tokens'
import { StartupMark } from './StartupMark'

/**
 * The first JS surface must be the same quiet material on every route that can
 * render before the navigator exists. It deliberately uses static gradients,
 * not blur or a looping animation: startup is a handoff, never a loading
 * screen, and Redmi should not spend frames compositing a decorative overlay.
 *
 * La secuencia importa. El sistema entrega una superficie PLANA con la marca ya
 * puesta; el primer frame de JS tiene que ser exactamente eso, sin movimiento,
 * o el cambio se lee como un flash y la marca como un salto. Por eso el
 * ambiente (gradiente, glows, halos) arranca invisible, espera a que el frame
 * nativo aterrice y recién entonces entra: lo único que cambia es la luz de
 * fondo, y la marca no se mueve ni parpadea en ningún momento del handoff.
 */
export const STARTUP_SURFACE = '#FBFAFD'

const STARTUP_GRADIENT = ['#FBFAFD', '#F5F1FB', '#FBFAFD'] as const
const PURPLE_GLOW = ['rgba(106,33,180,0.16)', 'rgba(106,33,180,0)'] as const
const CORAL_GLOW = ['rgba(240,66,45,0.14)', 'rgba(240,66,45,0)'] as const

/**
 * One ambient per LAUNCH, not per mount.
 *
 * The entry frame (`app/_layout.tsx`), the font gate and the auth gate are
 * separate mounts of this surface. A per-mount animation restarted the fade on
 * each one, so the light vanished and came back exactly at the handoff — a
 * flicker in the one moment that has to be invisible. The value lives at module
 * scope: the first surface starts it, every later frame continues it, and no
 * frame can replay or skip it.
 */
const launchAmbient = new Animated.Value(0)
let launchAmbientStarted = false

type StartupSurfaceProps = {
  /** `false` keeps the frame still: it joins the launch's ambient where it is. */
  animateMark?: boolean
  failed?: boolean
}

export function StartupSurface({ animateMark = true, failed = false }: StartupSurfaceProps) {
  const ambient = launchAmbient

  useEffect(() => {
    // Only one mount per launch owns the animation; the rest ride it. The
    // animation is deliberately NOT cancelled on unmount — it belongs to the
    // launch, not to this component instance.
    if (!animateMark || launchAmbientStarted) return
    launchAmbientStarted = true

    const animation = Animated.sequence([
      Animated.delay(DURATION.instant),
      Animated.timing(ambient, {
        toValue: 1,
        duration: DURATION.verySlow,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
    ])
    animation.start()
  }, [animateMark, ambient])

  const messageOpacity = useRef(new Animated.Value(0)).current

  useEffect(() => {
    if (!failed) return
    Animated.timing(messageOpacity, {
      toValue: 1,
      duration: DURATION.fast,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start()
  }, [failed, messageOpacity])

  return (
    <View style={styles.screen}>
      <Animated.View
        pointerEvents="none"
        style={[StyleSheet.absoluteFill, { opacity: ambient }]}
        testID="startup.ambient"
      >
        <LinearGradient
          colors={STARTUP_GRADIENT}
          start={{ x: 0.05, y: 0 }}
          end={{ x: 0.95, y: 1 }}
          style={StyleSheet.absoluteFill}
        />
        <LinearGradient
          colors={PURPLE_GLOW}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.purpleGlow}
        />
        <LinearGradient
          colors={CORAL_GLOW}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.coralGlow}
        />
        <View style={styles.haloOuter} />
        <View style={styles.haloInner} />
      </Animated.View>
      <StartupMark />
      {failed ? (
        <Animated.Text style={[styles.message, { opacity: messageOpacity }]}>
          No pudimos iniciar Tago. Cerrá y volvé a abrir la app.
        </Animated.Text>
      ) : null}
    </View>
  )
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: STARTUP_SURFACE,
    padding: 32,
  },
  purpleGlow: {
    position: 'absolute',
    width: 440,
    height: 440,
    borderRadius: 220,
    top: -240,
    left: -220,
    transform: [{ rotate: '-18deg' }],
  },
  coralGlow: {
    position: 'absolute',
    width: 420,
    height: 420,
    borderRadius: 210,
    right: -220,
    bottom: -230,
    transform: [{ rotate: '24deg' }],
  },
  haloOuter: {
    position: 'absolute',
    width: 260,
    height: 260,
    borderRadius: 130,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.72)',
    backgroundColor: 'rgba(255,255,255,0.08)',
  },
  haloInner: {
    position: 'absolute',
    width: 208,
    height: 208,
    borderRadius: 104,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.48)',
    backgroundColor: 'rgba(255,255,255,0.08)',
  },
  message: {
    maxWidth: 300,
    marginTop: 22,
    color: '#5F5D6B',
    fontSize: 16,
    lineHeight: 22,
    textAlign: 'center',
  },
})
